











































































































































































import { Component, Vue, Ref, Prop } from "vue-property-decorator";
import { findNickname, getActiveUser } from "@/data/repository/UserRepository";

import {
  downloadReportData,
  getCurrentProjectPhase,
  ProjectPhase,
} from "@/data/repository/ProjectRepository";
import {
  ProjectDetails,
  RepairAction,
  ApprovalResult,
  ProjectConclusionState,
  isPortalOnlyProject,
  ContactPersonInfo,
  formatProjectTypeName,
  getProjectTypeIdOfProject,
  ProjectTypeConfig,
  getDefaultProjectTypes,
  doesProjectTypeIdHaveDrying,
  doesProjectTypeIdHaveRestoration,
  doesProjectTypeIdHaveLeakDetection,
  doesProjectTypeIdHaveDamageAssessment,
  isRepairOnlyProjectTypeId,
  stripElectricityBillingPrefix
} from "@/data/models/ProjectDetails";
import { ProjectStatusData } from "@/data/models/ProjectStatusData";
import {
  saveProjectStep,
  saveContactPersonAppointment,
  OtherSteps,
} from "@/data/repository/ProjectStatusRepository";
import {
  formatDateShort,
  formatFullJsDate,
  formatDateAndMonth,
  formatJsDate,
  formatJsTime,
} from "@/utils/DateFormatter";
import { firestore } from "firebase/app";

import { formatContactPerson, joinToString } from "@/utils/LocalisationHelper";
import { formatContactPersonName } from "@/data/models/ProjectLog";
import { addFullStop, filterOutUndefined, formatNumber } from "@/utils/Utils";
import { ceilFromOneDecimal } from "@/utils/NumberRounding";
import {
  getClientId,
  isExternalSystem,
  getProjectManagement,
} from "@/data/repository/ClientRepository";
import { ButtonState } from "@/components/common/StateButton.vue";
import { parseDate } from "@/utils/DateHelper";

import ClientDataSectionComponent from "@/components/project/clientdata/ClientDataSection.vue";
import BaseStepDialogComponent from "./BaseStepDialog.vue";
import DownloadFileButton from "@/components/common/DownloadFileButton.vue";
import Report, {
  reportStorageLocationToDownloadRef,
  ReportType,
  ReportUsage,
} from "@/data/models/Report";
import { createPdfReportForProject } from "@/utils/pdf/PdfCreator";
import { ClientPanelAssignment } from "@/data/models/ClientPanelAssignment";
import { GenericDeviceProjectAssignment } from "@/data/models/GenericDeviceAssignment";
import { getProjectSettingsV2, ProjectSettingsOptions } from "@/data/models/Settings";
import { ProjectManagementSettingsModule } from "@/store/modules/ProjectManagementSettingsModule";
import { 
  COLOR_PROJECT_TIMELINE_STEP_APPIONTMENT_PENDING, 
  COLOR_PROJECT_TIMELINE_STEP_APPOINTMENT_INFO_MISSING, 
  COLOR_PROJECT_TIMELINE_STEP_DEFAULT, 
  COLOR_PROJECT_TIMELINE_STEP_DONE, 
  COLOR_PROJECT_TIMELINE_STEP_LOCKED, 
  COLOR_PROJECT_TIMELINE_STEP_OPEN, 
  COLOR_PROJECT_TIMELINE_STEP_SUSPENDED
} from "@/utils/colors";
import { themedFill, themedFillTextColor } from "@/utils/Theme";
import { getStepDependencies, hasStepStarted } from "@/data/models/ProjectSteps";
import { DamagePatternConclusionState } from "@/data/models/DryingProjectOverview";
import { getIsStepResumed, getIsStepSuspended, getProjectStepCreatedBy, getProjectStepCreationTimestamp, getResourceKey } from "@/data/models/ProjectState";
import { createAppointmentAccess } from "@/permissions/UserPermissions";
import { UserModule } from "@/store/modules/UserModule";
import { DownloadFileType } from "@/utils/FileTypeIcons";
import { formatTimelineStepSummary } from "@/utils/ProjectTimelineSummary";

enum ProjectStepStatus {
  LOCKED,
  APPOINTMENT_PENDING,
  APPOINTMENT_INFO_MISSING,
  DONE,
  OPEN,
  SUSPENDED,
}

/** The drying-area fields a stored step 9 may carry, in m². */
interface DryingAreaValues {
  dryingAreaFloor?: number | null;
  dryingAreaWallCeiling?: number | null;
}

@Component({
  components: {
    "base-step-dialog": BaseStepDialogComponent,
    "download-file-button": DownloadFileButton,
    "client-data-section": ClientDataSectionComponent,
  },
})
export default class ProjectStatusComponent extends Vue {
  projectManagement = getProjectManagement();

  @Prop({ default: false })
  showCompactVersion!: boolean;

  @Prop()
  projectDetails!: ProjectDetails;

  @Prop({ default: null })
  projectStatus!: ProjectStatusData | null;

  @Prop({ default: false })
  isReadOnlyMode!: boolean;

  @Ref("baseStepDialog")
  baseStepDialog!: BaseStepDialogComponent;

  @Prop()
  reports!: Array<Report>;

  @Prop({ default: [] })
  clientPanelAssignments!: Array<ClientPanelAssignment>;

  @Prop({ default: [] })
  genericDeviceAssignments!: Array<GenericDeviceProjectAssignment>;

  @Prop({ default: true })
  canEditProject!: boolean;

  @Prop({ default: () => [] })
  allowedSteps!: number[];

  subscriptions: Array<() => void> = [];

  showingSuccessSnackbar = false;
  successSnackbarText = "";

  stepDescriptionButtonState = ButtonState.STEP_BUTTON_COMPLETED;
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nextStepDescriptionButtonState(item: any) {
    const stepIndex = item.step;

    if (this.isAppointmentStep(stepIndex) && !this.canMakeNewAppointmentsInPortal) {
      return ButtonState.DISABLED;
    }
    else if (this.isFinalStep(stepIndex) && !this.canFinaliseProjectInPortal) {
      return ButtonState.DISABLED;
    }

    return this.isReadOnlyMode
      ? ButtonState.DISABLED
      : ButtonState.STEP_BUTTON_INCOMPLETE;
  }

  /**
   * File types of the step's download buttons, so each names what it produces.
   *
   * Exposed as properties because the enum itself is not available in the template.
   */
  get pdfFileType(): DownloadFileType {
    return DownloadFileType.PDF;
  }

  get csvFileType(): DownloadFileType {
    return DownloadFileType.CSV;
  }

  get showContactPersonChooser() {
    return this.projectDetails.clientData.contactPersons.length > 1;
  }

  isExternalSystem = isExternalSystem();

  clientId = getClientId();

  get canEdit() {
    return this.showCompactVersion || this.isExternalSystem;
  }

  get projectNumber() {
    const projectNumberErp = this.projectDetails.clientData.projectNumber;
    const projectNumberIres = this.projectDetails.clientData.projectNumberIres;

    return projectNumberErp && projectNumberErp !== "" 
      ? projectNumberErp
      : projectNumberIres;
  }

  get projectManagementSettings() {
    return ProjectManagementSettingsModule.projectManagementSettings;
  }

  get projectSettings() {
    return getProjectSettingsV2(this.projectManagementSettings);
  }

  get canEditProjectDetailsInPortal() {
    return this.projectSettings.editProjectsIn.includes(ProjectSettingsOptions.PORTAL);
  }

  get canFinaliseProjectInPortal() {
    return this.projectSettings.finaliseProjectsIn.includes(ProjectSettingsOptions.PORTAL);
  }

  get canMakeNewAppointmentsInPortal() {
    return this.projectSettings.newAppointmentsIn.includes(ProjectSettingsOptions.PORTAL);
  }

  get stepDependencies() {
    const projectTypeId = this.projectDetails.clientData?.projectTypeId ?? getProjectTypeIdOfProject(this.projectDetails);
    return getStepDependencies(projectTypeId);
  }

  isLastCPResultRow(index: number, stepNumber: number): boolean {
    const steps = this.filteredSteps.map(item => item.step);
    return steps.lastIndexOf(stepNumber) === index;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getShowDarkChip(item: any) {
    return (
      item.stepStatus !== ProjectStepStatus.OPEN &&
      item.stepStatus !== ProjectStepStatus.APPOINTMENT_INFO_MISSING
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getStepButtonState(step: any) {
    if (this.isReadOnlyMode) {
      return ButtonState.DISABLED;
    }

    if (this.isAppointmentStep(step.step) && !this.canMakeNewAppointmentsInPortal) {
      return ButtonState.DISABLED;
    }

    return step.stepStatus === ProjectStepStatus.DONE
      ? ButtonState.STEP_BUTTON_COMPLETED
      : step.stepStatus === ProjectStepStatus.APPOINTMENT_PENDING
        ? ButtonState.STEP_BUTTON_OVERDUE
        : ButtonState.STEP_BUTTON_INCOMPLETE;
  }

  /**
   * The step chip's fill for the active theme. The red of a pending or suspended step is a status,
   * so dark mode gives it the muted status red rather than the action red.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getChipColor(item: any) {
    return themedFill(this.getColor(item), "status");
  }

  /** Dark text on the palette fill in dark mode; light mode keeps the chip's forced white (`dark`). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getChipTextColor(item: any) {
    return themedFillTextColor(this.getColor(item), "", "status");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getColor(item: any) {
    const stepStatus = item.stepStatus;

    switch (stepStatus) {
      case ProjectStepStatus.LOCKED:
        return COLOR_PROJECT_TIMELINE_STEP_LOCKED;
      case ProjectStepStatus.OPEN:
        return COLOR_PROJECT_TIMELINE_STEP_OPEN;
      case ProjectStepStatus.APPOINTMENT_INFO_MISSING:
        return COLOR_PROJECT_TIMELINE_STEP_APPOINTMENT_INFO_MISSING;
      case ProjectStepStatus.DONE:
        return COLOR_PROJECT_TIMELINE_STEP_DONE;
      case ProjectStepStatus.APPOINTMENT_PENDING:
        return COLOR_PROJECT_TIMELINE_STEP_APPIONTMENT_PENDING;
      case ProjectStepStatus.SUSPENDED:
        return COLOR_PROJECT_TIMELINE_STEP_SUSPENDED;
    }

    return COLOR_PROJECT_TIMELINE_STEP_DEFAULT;
  }

  formatOrderApprovalOptions(approvalStatus: number) {
    if (approvalStatus == 0) {
      return this.$t("projectStatus.actionRejectOrder");
    } else {
      return this.$t("projectStatus.actionAcceptOrder");
    }
  }

  formatOrderApproval(approvalStatus: number) {
    if (approvalStatus == 0) {
      return this.$t("projectStatus.orderRejected");
    } else {
      return this.$t("projectStatus.orderAccepted");
    }
  }

  formatDryingApproval(approvalStatus: number, hasDrying = true) {
    if (approvalStatus == 0) {
      return this.$t(`projectStatus.approvalStatuses.${hasDrying ? "noDryingNecessary" : "noRestorationNecessary"}`).toString();
    } else if (approvalStatus == 1) {
      return this.$t("projectStatus.approvalStatuses.yesOffer").toString();
    } else {
      return this.$t("projectStatus.approvalStatuses.yesExecute").toString();
    }
  }

  formatProjectState(projectConclusionState: ProjectConclusionState) {
    switch (projectConclusionState) {
      case ProjectConclusionState.ARCHIVED:
        return this.$t("projectStatus.projectState.archived");
      case ProjectConclusionState.DELETED:
        return this.$t("projectStatus.projectState.deleted");
      case ProjectConclusionState.MOVED_TO_FINSIHED:
        return this.$t("projectStatus.projectState.movedToFinished");
      default:
        return this.$t("projectStatus.projectState.finished");
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatContactPersonInfoFromStep(step: any) {
    if (!this.showContactPersonChooser) {
      return undefined;
    }

    return this.formatContactPerson(step.contactPersonNumber);
  }

  formatContactPerson(contactPersonNumber: number) {
    return formatContactPerson(
      this,
      contactPersonNumber,
      this.projectDetails.clientData.contactPersons.find(
        (contactPerson) =>
          contactPerson.contactPersonNumber === contactPersonNumber
      )
    );
  }

  /**
   * Formats an option of the step dialog's flat picker as "Wohnung 1: Max Mustermann (EG)", the same
   * way the note dialog labels its flats, so one flat reads identically in both places. `0` is the
   * "all flats" option and keeps its own label; an unknown number falls back to the plain number.
   */
  formatContactPersonOption(contactPersonNumber: number) {
    if (!contactPersonNumber) {
      return this.$t("projectStatus.allApartments").toString();
    }

    const contactPerson = this.projectDetails.clientData.contactPersons.find(
      (it) => it.contactPersonNumber === contactPersonNumber
    );

    return contactPerson
      ? formatContactPersonName(this, contactPerson, false, this.projectDetails.clientData.contactPersons.length === 1)
      : this.formatContactPerson(contactPersonNumber);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shouldShowButton(step: any) {
    return step.showButton === false ? false : true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shouldAddMarginAfter(step: any) {
    return step.addMarginAfter === false ? false : true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatStepSectionTitle(step: any) {
    return step.description;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatStepSectionText(step: any) {
    if (!step.dateTime) {
      return "";
    }

    let nickname = findNickname(step.user);

    if (nickname === "Web") {
      nickname = "Master";
    }

    const user = nickname
      ? step.dateTime + " (" + nickname + ")"
      : step.dateTime;

    const parameters = step.parameters;

    if (parameters) {
      return [
        user,
        Array.isArray(parameters) ? parameters.join(", ") : parameters,
      ].join("\n");
    } else {
      return user;
    }
  }

  formatDryingSuccessful(dryingSuccessful: number) {
    return this.$t(`common.${dryingSuccessful == 0 ? "no" : "yes"}`);
  }

  formatInsuranceOfferResult(approvalStatus: number) {
    const approvedRes = this.hasDrying
      ? "dryingApproved"
      : "restorationApproved";
    const res = approvalStatus == 0 ? "noContract" : approvedRes;
    return this.$t(`projectStep.${res}`).toString();
  }

  /**
   * Drying areas are whole m², as the HyDry certificate reports them. A stored
   * value with decimals (the unrounded sums IRES Project writes, an API client, or
   * one saved while Office took 2 decimals) is rounded to one decimal, then up
   * (`ceilFromOneDecimal`), for display and for editing; a negative area is
   * meaningless and collapses to 0.
   *
   * @param value raw stored area in m²
   * @returns the whole-m² area, or `null` when there is no usable value
   */
  normalizeDryingArea(value?: number | null): number | null {
    if (value == null || !Number.isFinite(value)) {
      return null;
    }

    return Math.max(0, ceilFromOneDecimal(value));
  }

  /**
   * The two drying-area fields (Fläche Boden, Fläche Wand / Decke) shown beside the
   * drying duration in the step 7 and step 9 dialogs. Both steps show the project's
   * one pair: the step-9 value, else the project doc (`finishDamagePatternData`),
   * which IRES Project fills when the damage assessment is finished.
   *
   * @param step9 the stored step 9, if any
   * @param readonly whether both fields are read-only
   */
  dryingAreaNumberInputs(step9: DryingAreaValues | undefined, readonly: boolean) {
    const finishData = this.projectDetails?.finishDamagePatternData;

    return [
      {
        key: "dryingAreaFloor",
        title: this.$t(`projectStatus.dryingAreaFloor`).toString(),
        initialValue: this.normalizeDryingArea(
          step9?.dryingAreaFloor ?? finishData?.dryingAreaFloor
        ),
        cols: 4,
        readonly,
      },
      {
        key: "dryingAreaWallCeiling",
        title: this.$t(`projectStatus.dryingAreaWallCeiling`).toString(),
        initialValue: this.normalizeDryingArea(
          step9?.dryingAreaWallCeiling ?? finishData?.dryingAreaWallCeiling
        ),
        cols: 4,
        readonly,
      },
    ];
  }

  /**
   * The compact drying line of a step tile, e.g.
   * `Trocknungsdauer 14 Tage · Boden 43 m² · Wand / Decke 63 m²`. Each part shows
   * only when it has a value, falling back to the project doc like the dialogs.
   *
   * @param durationStep the step whose `estimatedDryingTime` is shown
   * @param step9 the stored step 9, if any, for the areas
   * @returns the line, or `""` when nothing has a value
   */
  formatDryingMetricsLine(
    durationStep: { estimatedDryingTime?: number | null } | undefined,
    step9: DryingAreaValues | undefined
  ): string {
    const finishData = this.projectDetails?.finishDamagePatternData;

    const estimatedDryingDurationValue = durationStep?.estimatedDryingTime
      ?? finishData?.estimatedDryingDurationInDays;
    const estimatedDryingDurationText = estimatedDryingDurationValue && estimatedDryingDurationValue > 0
      ? this.$tc("projectStatus.formatEstimatedDryingDuration", estimatedDryingDurationValue).toString()
      : undefined;

    const dryingAreaFloorValue = this.normalizeDryingArea(
      step9?.dryingAreaFloor ?? finishData?.dryingAreaFloor
    );
    const dryingAreaFloorText = dryingAreaFloorValue != null
      ? this.$t("projectStatus.formatDryingAreaFloor", [formatNumber(dryingAreaFloorValue)]).toString()
      : undefined;

    const dryingAreaWallCeilingValue = this.normalizeDryingArea(
      step9?.dryingAreaWallCeiling ?? finishData?.dryingAreaWallCeiling
    );
    const dryingAreaWallCeilingText = dryingAreaWallCeilingValue != null
      ? this.$t("projectStatus.formatDryingAreaWallCeiling", [formatNumber(dryingAreaWallCeilingValue)]).toString()
      : undefined;

    // Kept on a single line separated by "·" to minimise vertical space.
    return [estimatedDryingDurationText, dryingAreaFloorText, dryingAreaWallCeilingText]
      .filter((line) => !!line)
      .join(" · ");
  }

  canEditStep(stepStatus: ProjectStepStatus, stepIndex: number) {
    if (stepIndex == 2) {
      //Project approval can always be changed
      return true;
    }

    if (stepIndex == 3) {
      //First contact can be re-edited until the insurance offer (step 7) is made
      if (this.projectSteps["7"]) {
        return false;
      }
      return this.canEditProject;
    }

    if (stepIndex == 9 && this.isPortalOnlyProject) {
      // GZ projects: step 9 is auto-approved and stays editable while the project
      // is editable. The drying-duration field locks once drying setup (step 10)
      // exists, but the drying-area fields stay editable regardless — both enforced
      // per-field in the dialog config (onItemClicked case 9), not here.
      return this.canEditProject;
    }

    //Don't allow to change completed steps
    return stepStatus !== ProjectStepStatus.DONE && this.canEditProject;
  }

  /** Phone layout (below 600 px): the timeline renders as a compact list instead of the table. */
  get isPhone() {
    return this.$vuetify.breakpoint.xsOnly;
  }

  /** The phone row's caption, from the same time and creator columns the table shows. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getStepEntrySummary(item: any): string {
    return formatTimelineStepSummary(item.stepCreationTimestamps, item.stepCreators);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async onItemClicked(item: any) {
    if (!this.canEdit) {
      return;
    }

    if (item.stepStatus == ProjectStepStatus.LOCKED) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseDialogArgs: any = {
      title: item.description,
    };

    const stepIndex = item.step;

    const stepStatus = item.stepStatus;
    const projectStep =
      stepStatus == ProjectStepStatus.OPEN && stepIndex != 12 ? undefined : item.projectStep;

    switch (stepIndex) {
      case 1:
        if (item.projectStep) {
          baseDialogArgs["hideSaveButton"] = true;
        }
        break;
      case 2:
        baseDialogArgs["title"] = projectStep
          ? baseDialogArgs.title
          : this.$t("projectStatus.acceptOrder").toString();
        baseDialogArgs["selectionOption"] = {
          formatter: this.formatOrderApprovalOptions,
          options: [1, 0],
          selectedOption: projectStep?.orderAccepted,
          title: this.$t("projectStep.acceptOrder").toString(),
          requiresSelection: true,
        };
        break;
      case 3:
        baseDialogArgs["textInputOption"] = {
          title: this.$t("projectStep.damageDescription").toString(),
          initialText: projectStep?.damageDescription,
          type: "text",
        };
        break;
      case 4:
        baseDialogArgs["appointmentOption"] = {};
        baseDialogArgs.isContactPersonDependent = false;
        break;
      case 5:
        baseDialogArgs["reportData"] = {
          internalReportRef: projectStep?.downloadRefInternalPdfReport ?? null,
          externalReportRef: projectStep?.downloadRefExternalPdfReport ?? null,
          csvRef: undefined,
        };
        break;
      case 6:
        baseDialogArgs["appointmentOption"] = {};
        baseDialogArgs.isContactPersonDependent = false;
        break;
      case 7:
        baseDialogArgs["reportData"] = {
          internalReportRef: projectStep?.downloadRefInternalPdfReport ?? null,
          externalReportRef: projectStep?.downloadRefExternalPdfReport ?? null,
          csvRef: projectStep?.downloadRefServicesCsv ?? null,
        };

        baseDialogArgs["selectionOption"] = {
          formatter: this.formatDryingApproval,
          options: [1, 2, 0],
          selectedOption: projectStep?.approvalStatus ?? null,
          requiresSelection: true,
          title: this.$t(`projectStep.${this.containsDryingInfo ? "dryingNecessary" : "restorationNecessary"}`).toString(),
        };
        if (this.containsDryingInfo) {
          baseDialogArgs["textInputOption"] = {
            title: this.$t(`projectStatus.dryingDuration`).toString(),
            initialText: projectStep?.estimatedDryingTime,
            type: "number",
            cols: 4,
          };
          // The same row as step 9, because a project need not have an insurance
          // step. It shows the one pair step 9 shows, never a second one. Always
          // read-only: Office never edits an existing step 7 (IRES Project writes
          // it), and the case-7 save does not carry the areas, so an editable field
          // would be silently discarded. The pair is edited in step 9.
          baseDialogArgs["numberInputs"] = this.dryingAreaNumberInputs(this.projectSteps["9"], true);
        }

        break;
      case 9:
        // GZ projects: step 9 is auto-approved — only show drying duration editor
        if (!this.isPortalOnlyProject) {
          baseDialogArgs["selectionOption"] = {
            formatter: this.formatInsuranceOfferResult,
            options: [2, 0],
            selectedOption: projectStep?.approvalStatus ?? null,
            requiresSelection: true,
            title: this.$t(`projectStatus.${this.containsDryingInfo ? "releaseDrying" : "releaseRestoration"}`).toString(),
          };
        }
        if (this.containsDryingInfo) {
          baseDialogArgs["textInputOption"] = {
            title: this.$t(`projectStatus.dryingDuration`).toString(),
            initialText:
              projectStep?.estimatedDryingTime ??
              this.projectSteps["7"]?.estimatedDryingTime ??
              this.projectManagementSettings?.projectEditingSettings?.defaultDryingDurationInDays,
            type: "number",
            cols: 4,
            // Portal-only: drying duration locks once drying setup (step 10) exists,
            // while the drying-area fields below stay editable.
            readonly: this.isPortalOnlyProject && !!this.projectSteps["10"],
          };
          // Drying areas (m²): always shown, but editable only on portal-only projects.
          baseDialogArgs["numberInputs"] = this.dryingAreaNumberInputs(
            projectStep,
            !this.isPortalOnlyProject
          );
        }
        break;
      case 15:
        baseDialogArgs["selectionOption"] = {
          formatter: this.formatDryingSuccessful,
          options: [1, 0],
          selectedOption: projectStep?.dryingSuccessful ?? null,
          requiresSelection: true,
          title: this.$t("projectStep.dryingSuccessful").toString(),
        };
        baseDialogArgs["textInputOption"] = {
          title: this.$t("projectStep.note").toString(),
          initialText: projectStep?.note,
          type: "text",
        };
        break;
      case 10:
      case 12:
      case 14:
      case 16:
        baseDialogArgs["appointmentOption"] = {};
        break;
      case 18:
        baseDialogArgs["datePickerOption"] = {
          title: this.$t("projectStep.projectEnd").toString(),
          selectedDate: projectStep?.projectEndDate
            ? firestore.Timestamp.fromDate(
                parseDate(projectStep.projectEndDate)
              )
            : undefined,
        };

        baseDialogArgs["reportData"] = {
          internalReportRef: projectStep?.downloadRefInternalPdfReport ?? null,
          externalReportRef: projectStep?.downloadRefExternalPdfReport ?? null,
          csvRef: projectStep?.downloadRefServicesCsv ?? null,
        };

        break;
    }

    if (stepIndex >= 10 && stepIndex < 18) {
      baseDialogArgs.isContactPersonDependent = true;
    }

    if (baseDialogArgs.isContactPersonDependent) {
      if (this.projectDetails.clientData.contactPersons.length > 1) {
        const isContactPersonResultStepIndex = this.isContactPersonResultStepIndex(
          stepIndex
        );

        let contactPersonsCompleted = isContactPersonResultStepIndex
          ? this.getContactPersonsNumberCompleted(
              this.projectSteps[stepIndex.toString()]
            )
          : new Set<number>();

        if (
          !isContactPersonResultStepIndex &&
          this.isIndexOfContactPersonAppointmentStep(stepIndex) &&
          stepIndex !== 12
        ) {
          contactPersonsCompleted = this.getContactPersonsNumberCompleted(
            this.projectSteps[(1 + stepIndex).toString()]
          );
        }

        baseDialogArgs["contactPersons"] = {
          formatter: this.formatContactPersonOption,
          options: [
            0,
            ...this.projectDetails.clientData.contactPersons
              .map((contactPerson) => contactPerson.contactPersonNumber)
              .filter(
                (contactPersonNumber) =>
                  !contactPersonsCompleted.has(contactPersonNumber)
              ),
          ],
          selectedOption: projectStep?.contactPersonNumber ?? 0,
          requiresSelection: true,
          title: this.$t("clientData.contactPerson").toString(),
        };
      }
    }

    if (projectStep?.createdBy) {
      baseDialogArgs["createdBy"] = item.projectStep.createdBy;
    }

    if (projectStep?.creationTimestamp) {
      baseDialogArgs["creationDate"] = parseDate(projectStep.creationTimestamp);
    }

    if (projectStep?.appointmentCreatedBy) {
      baseDialogArgs["appointmentCreatedBy"] = item.projectStep.appointmentCreatedBy;
    }

    if (projectStep?.appointmentCreationTimestamp) {
      baseDialogArgs["appointmentCreationTimestamp"] = parseDate(projectStep.appointmentCreationTimestamp);
    }

    if (baseDialogArgs.appointmentOption && projectStep) {
      baseDialogArgs.appointmentOption["currentAppointment"] = item.projectStep;
    }

    let isEditEnabled = this.canEditStep(stepStatus, stepIndex);

    if (
      stepStatus == ProjectStepStatus.DONE &&
      this.isAppointmentStepIndex(stepIndex)
    ) {
      const projectPhase = getCurrentProjectPhase(this.projectDetails);

      if (
        (stepIndex == 4 && projectPhase <= ProjectPhase.LEAK_DETECTION) ||
        (stepIndex == 6 && projectPhase <= ProjectPhase.DAMAGE_ASSESSMENT)
      ) {
        isEditEnabled = true;

        baseDialogArgs.createdBy = null;
        baseDialogArgs.creationTimestamp = null;

        if (baseDialogArgs.appointmentOption) {
          baseDialogArgs.appointmentOption.currentAppointment = null;
        }
      }
    }

    if (
      this.isAppointmentStepIndex(stepIndex) &&
        !createAppointmentAccess(getActiveUser(), this.projectSettings)
    ) {
      if (stepStatus === ProjectStepStatus.OPEN) {
        return;
      }

      isEditEnabled = false;
    }

    baseDialogArgs["editEnabled"] = isEditEnabled;
    baseDialogArgs["projectDetails"] = this.projectDetails;
    baseDialogArgs["stepIndex"] = item.step;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = await this.baseStepDialog.show(baseDialogArgs);

    await this.addReportInfo(result, stepIndex);

    if (result) {
      switch (stepIndex) {
        case 2: {
          await this.saveProjectStep(stepIndex, {
            createdBy: result.createdBy,
            creationTimestamp: result.creationTimestamp,
            orderAccepted: result.selectedOption,
          });

          this.$emit(
            "projectApproved",
            result.selectedOption
          );
          break;
        }
        case 3: {
          const existingStep3 = item.projectStep;
          const isReEdit = !!existingStep3?.createdBy;
          await this.saveProjectStep(stepIndex, {
            createdBy: isReEdit ? existingStep3.createdBy : result.createdBy,
            creationTimestamp: isReEdit ? existingStep3.creationTimestamp : result.creationTimestamp,
            damageDescription: result.textInput,
            ...(isReEdit ? {
              editedBy: UserModule.activeUserIdentifier,
              editTimestamp: new Date().toISOString(),
            } : {}),
          });
          break;
        }
        case 5: {
          await this.saveProjectStep(stepIndex, result);
          break;
        }
        case 7: {
          await this.saveProjectStep(stepIndex, {
            createdBy: result.createdBy,
            creationTimestamp: result.creationTimestamp,
            downloadRefExternalPdfReport: result.downloadRefExternalPdfReport,
            downloadRefInternalPdfReport: result.downloadRefInternalPdfReport,
            downloadRefServicesCsv: result.downloadRefServicesCsv,
            approvalStatus: result.selectedOption,
            estimatedDryingTime:
              result.textInput && result.textInput !== ""
                ? Number(result.textInput)
                : null,
          });
          break;
        }
        case 9: {
          // Step 9 is rewritten wholesale on save, so preserve all existing step
          // fields (createdBy, creationTimestamp, approvalStatus, source, ...) and
          // overlay ONLY the fields this edit actually changed. Creation metadata is
          // stamped only when the step is first created.
          // Mirror the dialog's per-field editability (see onItemClicked case 9):
          // duration locks once step 10 exists on portal-only projects; areas are
          // editable only on portal-only projects.
          const durationLocked = this.isPortalOnlyProject && !!this.projectSteps["10"];
          const areasEditable = this.isPortalOnlyProject;
          await this.saveProjectStep(stepIndex, {
            ...(projectStep ?? {
              createdBy: result.createdBy,
              creationTimestamp: result.creationTimestamp,
            }),
            // Non-portal projects can change the release selection; portal-only has
            // none, so fall back to the preserved (auto-)approval.
            approvalStatus: result.selectedOption ?? projectStep?.approvalStatus,
            ...(durationLocked
              ? {}
              : {
                  estimatedDryingTime:
                    result.textInput && result.textInput !== ""
                      ? Number(result.textInput)
                      : null,
                }),
            ...(areasEditable
              ? {
                  // Already whole m² and floored at 0 by the dialog, which owns the
                  // locale format a pasted value is read in.
                  dryingAreaFloor: result.numberInputs?.dryingAreaFloor ?? null,
                  dryingAreaWallCeiling:
                    result.numberInputs?.dryingAreaWallCeiling ?? null,
                }
              : {}),
          });
          break;
        }
        case 10:
        case 12:
        case 14:
        case 16:
          await this.saveContactPersonAppointment(stepIndex, result, true);
          break;
        case 15:
          await this.saveProjectStep(stepIndex, {
            createdBy: result.createdBy,
            creationTimestamp: result.creationTimestamp,
            downloadRefExternalPdfReport: result.downloadRefExternalPdfReport,
            downloadRefInternalPdfReport: result.downloadRefInternalPdfReport,
            contactPersonNumber: result.contactPersonNumber ?? 0,
            dryingSuccessful: result.selectedOption,
            note: result.textInput,
          });
          break;
        case 18:
          await this.saveProjectStep(stepIndex, {
            createdBy: result.createdBy,
            contactPersonNumber: result.contactPersonNumber ?? 0,
            downloadRefExternalPdfReport: result.downloadRefExternalPdfReport,
            downloadRefInternalPdfReport: result.downloadRefInternalPdfReport,
            downloadRefServicesCsv: result.downloadRefServicesCsv,
            creationTimestamp: result.creationTimestamp,
            projectEndDate: result.selectedDate,
          });
          break;
        default:
          await this.saveProjectStep(stepIndex, result);
          break;
      }

      if (this.isAppointmentStepIndex(stepIndex)) {
        this.successSnackbarText = this.$t("projectStatus.appointmentSaveSuccess").toString();
        this.showingSuccessSnackbar = true;
      }
    }

    if (result && item.step == 18) {
      await this.markAllContactPersonAppointmentsAsCompleted();
    }
  }

  getOtherStepsAffectedByStepUpdate(
    stepIndex: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result: any
  ): OtherSteps[] {
    const appointmentIndex = stepIndex - 1;
    if (result && this.isAppointmentStepIndex(appointmentIndex)) {
      if (this.isIndexOfContactPersonAppointmentStep(appointmentIndex)) {
        const contactPersonNumber = result.contactPersonNumber ?? 0;

        const appointmentIndex = stepIndex - 1;
        const appointments = this.projectSteps[appointmentIndex.toString()];

        let wasAppointmendCompleted = false;

        if (appointments && Array.isArray(appointments)) {
          for (const appointment of appointments) {
            if (
              !appointment.completed &&
              (appointment.contactPersonNumber === contactPersonNumber ||
                contactPersonNumber === 0)
            ) {
              appointment.completed = true;
              wasAppointmendCompleted = true;
            }
          }
        }

        if (wasAppointmendCompleted) {
          return [
            {
              stepIndex: appointmentIndex.toString(),
              data: appointments,
            },
          ];
        }
      } else {
        const appointment = this.projectSteps[appointmentIndex.toString()];

        if (appointment && !appointment.completed) {
          appointment.completed = true;
          appointment.contactPersonNumber = undefined;

          return [
            {
              stepIndex: appointmentIndex.toString(),
              data: appointment,
            },
          ];
        }
      }
    }
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async addReportInfo(result: any, stepIndex: number) {
    if (!result) {
      return;
    }

    if (this.showCompactVersion) {
      return;
    }

    if (
      stepIndex === 5 ||
      stepIndex === 7 ||
      stepIndex === 15 ||
      stepIndex === 18
    ) {
      if (this.reports) {
        const latestReport = this.reports[0];

        if (latestReport) {
          result.downloadRefExternalPdfReport = reportStorageLocationToDownloadRef(
            latestReport.storageLocation
          );
          result.downloadRefInternalPdfReport = reportStorageLocationToDownloadRef(
            latestReport.storageLocation
          );
        }
      }
    }

    if (
      getClientId() === "sfztT2VTtAeNOpcgmdWuIQea4Ve2" &&
      (stepIndex === 7 ||
        stepIndex === 18 ||
        stepIndex === 11 ||
        stepIndex === 15)
    ) {
      if (stepIndex !== 18) {
        result.downloadRefServicesCsv =
          "CSV_TEST/Korbacher Str._Gewerke_2105110853.csv";
      } else if (stepIndex === 18) {
        result.downloadRefServicesCsv =
          "CSV_TEST/Löwenzahnweg_Gewerke_2105041556_1.csv";
      }
    
      const reportDate = new Date();
      const timestamp = firestore.Timestamp.fromDate(reportDate);

      const report = await createPdfReportForProject(
        this,
        this.projectDetails,
        stepIndex === 7 ? ReportType.DAMAGE_REPORT : ReportType.FINAL_REPORT,
        ReportUsage.EXTERNAL,
        timestamp
      );

      const tokens = report.storageLocation.split("/");
      const ref = tokens[tokens.length - 2] + "/" + tokens[tokens.length - 1];

      if (stepIndex === 11 || stepIndex === 15) {
        result.downloadRefInternalPdfReport = ref;
      } else {
        result.downloadRefExternalPdfReport = ref;
      }
    }
  }

  async saveContactPersonAppointment(
    stepIndex: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    projectStep: any,
    updateProjectStatus: boolean
  ) {
    await saveContactPersonAppointment(
      this.projectDetails,
      stepIndex,
      projectStep,
      updateProjectStatus,
      this.projectStatus ?? undefined
    );
  }

  async saveProjectStep(
    stepIndex: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    projectStep: any,
    updateProjectStatus?: boolean
  ) {
    const isAppointmentStep = this.isAppointmentStep(stepIndex);

    if (isAppointmentStep) {
      this.$emit("appointmentUpdated");
    }

    await saveProjectStep(
      this.projectDetails,
      stepIndex,
      filterOutUndefined(projectStep),
      updateProjectStatus ?? true,
      {
        currentData: this.projectStatus ?? undefined,
        otherSteps: this.getOtherStepsAffectedByStepUpdate(
          stepIndex,
          projectStep
        ),
      }
    );
  }

  get compactHeaders() {
    return [
      {
        text: this.$t("projectStatus.dateTime"),
        value: "dateTime",
      },
      {
        text: this.$t("projectStatus.projectStepDescription"),
        sortable: false,
        value: "description",
      },
      {
        text: this.$t("projectStatus.parameters"),
        sortable: false,
        value: "parameters",
      },
    ];
  }

  getStyle(stepIndex: number) {
    return this.isAppointmentStepIndex(stepIndex) ? { 'white-space': 'pre-line' } : {};
  }

  get containsDryingInfo() {
    return doesProjectTypeIdHaveDrying(this.projectTypeId);
  }

  get containsLeakDetectionInfo() {
    return doesProjectTypeIdHaveLeakDetection(this.projectTypeId);
  }

  get containsDamageAssessmentInfo() {
    return doesProjectTypeIdHaveDamageAssessment(this.projectTypeId);
  }

  get isRepairOnlyProject() {
    return isRepairOnlyProjectTypeId(this.projectTypeId);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getProjectStep(step: any, stepIndex: number) {
    return stepIndex === 17 && step?.extendedRepairResult
      ? step.extendedRepairResult
      : step;
  }

  get headers() {
    return [
      {
        text: this.$t("projectStatus.step"),
        align: "start",
        sortable: false,
        value: "step",
      },
      {
        text: this.$t("projectStatus.projectStepDescription"),
        sortable: false,
        value: "description",
      },
      {
        text: this.$t("projectStatus.dateTime"),
        value: "stepCreationTimestamps",
      },
      {
        text: this.$t("projectStatus.user"),
        value: "stepCreators",
      },
      {
        text: this.$t("projectStatus.parameters"),
        sortable: false,
        value: "parameters",
      },
    ];
  }

  /**
   * Sorts step details by their individual creation timestamps.
   *
   * Each multiline string in `stepCreationTimestamps` is split into individual entries.
   * These entries are parsed into Dates and then sorted chronologically. Corresponding
   * `stepCreators` and `parameters` values are split by line and aligned with the sorted timestamps.
   *
   * @param {any} row - The row object containing step details with arrays of multiline timestamps, creators, and parameters.
   * @returns {any} A new row object with flattened and sorted `stepCreationTimestamps`, `stepCreators`, and `parameters`.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortStepDetailsByTimestamp(row: any): any {
    const parseDate = (str: string): Date => {
      const match = str.match(/^(\d{2})\.(\d{2})\.(\d{2}) (\d{2}):(\d{2})$/);
      if (!match) return new Date(0);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_, day, month, year, hour, minute] = match.map(Number);
      return new Date(2000 + year, month - 1, day, hour, minute);
    };

    if (
      !row ||
      !Array.isArray(row.stepCreationTimestamps) ||
      !Array.isArray(row.stepCreators) ||
      !Array.isArray(row.parameters)
    ) {
      return row;
    }

    // Single-entry steps don't need sorting and splitting by \n would
    // drop multi-line parameter text (e.g. drying duration on step 9).
    if (
      row.stepCreationTimestamps.length <= 1 &&
      row.stepCreators.length <= 1 &&
      row.parameters.length <= 1
    ) {
      return row;
    }

    const flat: Array<{
      timestamp: string;
      parsed: Date;
      creator: string;
      parameter: string;
    }> = [];

    row.stepCreationTimestamps.forEach((timestampBlock: string, i: number) => {
      const timestampLines = timestampBlock.split("\n").map(l => l.trim()).filter(Boolean);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const creatorLines = (row.stepCreators[i] ?? "").split("\n").map((l: any) => l.trim());
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const parameterLines = (row.parameters[i] ?? "").split("\n").map((l: any) => l.trim());

      for (let j = 0; j < timestampLines.length; j++) {
        flat.push({
          timestamp: timestampLines[j],
          parsed: parseDate(timestampLines[j]),
          creator: creatorLines[j] ?? "",
          parameter: parameterLines[j] ?? ""
        });
      }
    });

    flat.sort((a, b) => a.parsed.getTime() - b.parsed.getTime());

    return {
      ...row,
      stepCreationTimestamps: flat.map(item => item.timestamp),
      stepCreators: flat.map(item => item.creator),
      parameters: flat.map(item => item.parameter),
    };
  }

  get projectTypeId() {
    return this.projectDetails.clientData?.projectTypeId ?? getProjectTypeIdOfProject(this.projectDetails);
  }

  get hasRestoration() {
    return doesProjectTypeIdHaveRestoration(this.projectTypeId);
  }

  get steps() {
    const projectSteps = this.projectStatus?.projectSteps ?? {};
    const projectDetails = this.projectDetails;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: Array<any> = [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resultMap = new Map<number, any>();

    // Derive which steps to render from the stepDependencies map so we always
    // show the relevant steps for the current project type.
    const stepIndices = Array.from(this.stepDependencies.keys()).sort((a, b) => a - b);

    for (const stepIndex of stepIndices) {
      const row = this.createStepRow(stepIndex, projectSteps, resultMap, projectDetails);

      result.push(row);
      resultMap.set(stepIndex, row);
    }

    return result;
  }

  get isPortalOnlyProject() {
    return isPortalOnlyProject(this.projectDetails);
  }

  /** Whether step 6 (damage assessment appointment) has data (DONE or APPOINTMENT_PENDING). */
  get isStep6Done() {
    return !!this.projectStatus?.projectSteps["6"];
  }

  get nextSteps() {
    const steps = this.steps;

    let indexOfLatestDoneStep = -1;

    steps.forEach((step) => {
      if (
        step.stepStatus === ProjectStepStatus.DONE ||
        step.stepStatus === ProjectStepStatus.APPOINTMENT_PENDING ||
        step.stepStatus === ProjectStepStatus.APPOINTMENT_INFO_MISSING
      ) {
        indexOfLatestDoneStep = step.step;
      }
    });

    if (indexOfLatestDoneStep < 0) {
      return [];
    }

    const completableSteps = [3, 8, 9, 10];

    const isDryingSetupComplete = this.isPortalOnlyProject
      ? hasStepStarted(this.projectSteps["11"])
      : this.projectDetails.setupDryingData?.complete === true;

    const isDryingConclusionComplete = this.isPortalOnlyProject
      ? hasStepStarted(this.projectSteps["15"])
      : this.projectDetails.packupDevicesData?.complete === true;

    // TODO: Change this utilising knowledge about extended project scope.
    if (isDryingSetupComplete) {
      if (!isDryingConclusionComplete) {
        completableSteps.push(14);
      }

      if (this.showRepairAppointment) {
        completableSteps.push(16);
      }
    }
    else if (
      !this.hasDrying && 
      this.hasRestoration &&
      this.projectDetails.finishDamagePatternData?.conclusionState === DamagePatternConclusionState.CARRY_OUT_DRYING
    ) {
      completableSteps.push(16);
    }

    if (this.containsLeakDetectionInfo) {
      completableSteps.push(4);
    }

    if (this.containsDamageAssessmentInfo) {
      completableSteps.push(6);
    }

    // Steps 4 (leak detection appointment) and 6 (damage assessment appointment) are parallel —
    // they share the same prerequisites and should not block each other via indexOfLatestDoneStep.
    const parallelAppointmentSteps = [4, 6];

    // When step 6 is done and step 4 is open, step 4 is already shown in filteredSteps
    // (before step 6) to maintain correct ordering. Exclude it from nextSteps to avoid duplication.
    const stepsAlreadyInFilteredSteps = (this.isStep6Done && this.containsLeakDetectionInfo) ? [4] : [];

    const nextSteps = steps.filter(
      (step) =>
        (step.step > indexOfLatestDoneStep || parallelAppointmentSteps.includes(step.step)) &&
        step.stepStatus === ProjectStepStatus.OPEN &&
        completableSteps.indexOf(step.step) >= 0 &&
        !stepsAlreadyInFilteredSteps.includes(step.step)
    );

    return nextSteps;
  }

  get isProjectRejected() {
    return this.projectDetails?.clientData?.projectApproval?.approvalResult === ApprovalResult.REJECTED;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get displayedFilteredSteps(): any[] {
    if (!this.allowedSteps || this.allowedSteps.length === 0) return this.filteredSteps;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.filteredSteps.filter((s: any) => this.allowedSteps.includes(s.step));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get displayedNextSteps(): any[] {
    if (!this.allowedSteps || this.allowedSteps.length === 0) return this.nextSteps;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.nextSteps.filter((s: any) => this.allowedSteps.includes(s.step));
  }

  get filteredSteps() {
    const steps = this.steps;

    const wasProjectApproved = steps[1]?.stepStatus === ProjectStepStatus.DONE;
    const wasOfferApproved = steps[8]?.stepStatus === ProjectStepStatus.DONE;

    const filtered = steps.filter(
      (step) =>
        (!wasProjectApproved || step.step !== 1) &&
        (!wasOfferApproved || step.step !== 8) &&
        this.showStep(step, steps)
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let result: any[] = [];

    for (const step of filtered) {
      if (this.isContactPersonAppointmentStep(step)) {
        result = [...result, ...this.expandAppointmentStep(step)];
      } else {
        result.push(step);
      }
    }

    return result.sort((s1, s2) => {
      // Parallel appointment steps (leak detection before damage assessment) always sort by step index
      const parallelAppointmentSteps = [4, 6];
      if (parallelAppointmentSteps.includes(s1.step) && parallelAppointmentSteps.includes(s2.step)) {
        return s1.step - s2.step;
      }

      let sortS1Value = s1.creationDate;
      let sortS2Value = s2.creationDate;

      if (s1.step < 9) {
        sortS1Value = sortS1Value / 1000;
      }

      if (s2.step < 9) {
        sortS2Value = sortS2Value / 1000;
      }

      return sortS1Value - sortS2Value;
    });
  }

  // eslint-disable-next-line
  showStep(step: any, steps: any) {
    /* if (
      step.step === 2 &&
      step.stepStatus === ProjectStepStatus.DONE &&
      this.projectManagement === ProjectManagement.DEFAULT
    ) {
      //Don't want to display project approval if project approval is done and user uses project manager
      return false;
    } */

    // We handle project approval from the project section
    if (
      step.step === 2
    ) {
      return false;
    }

    const stepStatus = step.stepStatus;
    const stepIndex = step.step;

    if (stepIndex === 13) {
      //Step gets shown via expandAppointmentStep
      return false;
    }

    if (stepIndex === 6 && this.projectStatus?.projectSteps["7"]) {
      return false;
    }

    if (
      !(
        stepStatus === ProjectStepStatus.DONE ||
        stepStatus === ProjectStepStatus.APPOINTMENT_PENDING ||
        stepStatus === ProjectStepStatus.APPOINTMENT_INFO_MISSING
      )
    ) {
      const dryingStarted = hasStepStarted(this.projectSteps["11"]);
      const projectFinished = hasStepStarted(this.projectSteps["18"]);

      if (step.step === 12 && dryingStarted && !projectFinished) {
        return true;
      }

      // Show step 4 (leak detection appointment) in filteredSteps when step 6 (damage assessment
      // appointment) is already done, so that step 4's button renders before step 6 in the list.
      if (step.step === 4 && this.containsLeakDetectionInfo && this.isStep6Done) {
        return true;
      }

      return false;
    }

    if (this.containsLeakDetectionInfo) {
      if (stepIndex === 4 && this.projectStatus?.projectSteps["5"]) {
        return false;
      }
    }

    if (this.hasDrying || this.hasRestoration) {
      if (
        this.isIndexOfContactPersonAppointmentStep(stepIndex - 1) &&
        this.projectStatus?.projectSteps[(stepIndex - 1).toString()]
      ) {
        return false;
      }
    }

    /* if (this.hasRestoration) {
      if (stepIndex === 16 && this.projectStatus?.projectSteps["17"]) {
        return false;
      }
    } */

    // TODO: Verify this
    if (this.isAppointmentStepIndex(stepIndex)) {
      if (stepIndex === 16 && !this.showRepairAppointment) {
        return false;
      }
    }

    return true;
  }

  get showRepairAppointment() {
    return (
      this.projectDetails?.projectConclusionData?.repairAction !==
        RepairAction.NOT_NEEDED /* &&
      !!this.projectDetails?.projectConclusionData?.repairCompletedBy */
    );
  }

  get projectSteps() {
    return this.projectStatus?.projectSteps ?? {};
  }

  get areAllAssignmentsClosed() {
    const openClientPanelAssignments = this.clientPanelAssignments.filter(assignment => !assignment.assignmentEndDate);
    const openGenericDeviceAssignments = this.genericDeviceAssignments.filter(assignment => !assignment.assignmentEndDate);

    return openClientPanelAssignments.length === 0 && openGenericDeviceAssignments.length === 0; 
  }

  get unfilteredProjectTypes() {
    const fromStore = this.projectManagementSettings;
    const loaded: ProjectTypeConfig[] | undefined = fromStore && fromStore.projectTypes;
    const base = JSON.parse(JSON.stringify(getDefaultProjectTypes())) as ProjectTypeConfig[];

    if (loaded && Array.isArray(loaded)) {
      const map = new Map<number, ProjectTypeConfig>(loaded.map(p => [p.projectTypeId, p]));
      return base.map(p => {
        const saved = map.get(p.projectTypeId);
        return saved ? { ...p, ...saved } : p;
      });
    } else {
      return base;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isAppointmentStep(step: any) {
    if (!step) {
      return false;
    }

    return this.isAppointmentStepIndex(step);
  }

  isFinalStep(index: number) {
    return index === 18;
  }

  appointmentIndices = [4, 6, 10, 12, 14, 16];
  appointmentResultIndices = [5, 7, 11, 13, 15, 17];
  cotactPersonAppointmentIndices = [10, 12, 14, 16];
  indicesToBeCheckedForReopenedPhases = [5, 7, 11, 13, 15, 17];
  indicesToBeCheckedForSuspendedPhases = [5, 7, 11, 13, 15, 17];

  isAppointmentStepIndex(index: number) {
    return this.appointmentIndices.indexOf(index) >= 0;
  }

  isAppointmentResultStepIndex(index: number) {
    return this.appointmentResultIndices.indexOf(index) >= 0;
  }

  isContactPersonResultStepIndex(index: number) {
    return [11, 15, 17, 18].indexOf(index) >= 0;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isContactPersonAppointmentStep(step: any) {
    return this.isIndexOfContactPersonAppointmentStep(step.step);
  }

  isIndexOfContactPersonAppointmentStep(index: number) {
    return this.cotactPersonAppointmentIndices.indexOf(index) >= 0;
  }

  shouldCheckForReopenedPhases(index: number) {
    return this.indicesToBeCheckedForReopenedPhases.includes(index);
  }

  shouldCheckForSuspendedPhases(index: number) {
    return this.indicesToBeCheckedForSuspendedPhases.includes(index);
  }

  createRowContent(
    stepIndex: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    projectStep: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resultMap: Map<number, any>,
    projectDetails: ProjectDetails
  ) {
    let res = "projectStatus.";
    const isCompactVersion = this.showCompactVersion;
    const isAppointmentStep = this.isAppointmentStepIndex(stepIndex);
    const isAppointmentResultStep = this.isAppointmentResultStepIndex(stepIndex);

    if (isCompactVersion && isAppointmentStep) {
      if (!projectStep?.completed || stepIndex === 12) {
        res += "appointmentS";
      }
      else {
        res += "ongoingS";
      }
    }
    else if (isCompactVersion && isAppointmentResultStep) {
      const isStepResumed = getIsStepResumed(projectStep, projectStep?.phaseReOpened);
      const isStepSuspended = getIsStepSuspended(projectStep, projectStep?.phaseReOpened);

      if (isStepResumed) {
        res += "resumedS";
      }
      else if (isStepSuspended) {
        res += "suspendedS";
      }
      else {
        res += "concludedS";  
      }
    }
    else {
      res += "s";
    }

    res += `tep${stepIndex}`;

    if (!this.hasDrying && stepIndex === 9) {
      res += "Restoration";
    }

    let description = this.$t(
        `${res}`
      ).toString();

    if (stepIndex === 2 && this.showCompactVersion) {
      if (projectStep) {
        description = this.formatOrderApproval(
          projectStep.orderAccepted
        ).toString();
      } else {
        description = this.$t(`projectStep.approveProject`).toString();
      }
    }

    if (stepIndex === 13 && projectStep?.creationTimestamp) {
      const stepDate = parseDate(projectStep.creationTimestamp);

      if (stepDate) {
        const dryingConclusionDate = projectDetails.projectConclusionData?.dryingSuccessfulSetBy?.recordedAt?.toDate();

        if (
          dryingConclusionDate &&
          stepDate.getTime() > dryingConclusionDate.getTime()
        ) {
          description = this.$t("projectStatus.controlMeasurement").toString();
        }
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rowContent: any = {
      step: stepIndex,
      description: description,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let steps: any[] = projectStep ? [projectStep] : [];

    if (Array.isArray(projectStep)) {
      steps = projectStep;
      projectStep = projectStep[projectStep.length - 1];
    }

    const projectStepCreationTimestamp = getProjectStepCreationTimestamp(projectStep);
    const projectStepCreatedBy = getProjectStepCreatedBy(projectStep);

    const creationTimestamp = projectStepCreationTimestamp && projectStepCreationTimestamp !== ""
      ? projectStepCreationTimestamp
      : projectStep?.appointmentCreationTimestamp;
    const createdBy = projectStepCreatedBy && projectStepCreatedBy !== ""
      ? projectStepCreatedBy
      : projectStep?.appointmentCreatedBy;

    rowContent["creationDate"] = stepIndex;
    parseDate(creationTimestamp)?.getTime() ??
      stepIndex * 1000000000;

    if (
      projectStep && 
      (
        projectStepCreationTimestamp || 
        projectStep.appointmentCreationTimestamp
      )
    ) {
      rowContent["user"] = createdBy;
      rowContent["stepCreators"] = steps.map((step) =>
        joinToString(
          [
            findNickname(step.appointmentCreatedBy),
            findNickname(getProjectStepCreatedBy(step)),
          ],
          "\n"
        )
      );

      const date = parseDate(creationTimestamp);

      rowContent["dateTime"] = this.showCompactVersion
        ? formatDateAndMonth(date)
        : formatFullJsDate(date);

      rowContent["stepCreationTimestamps"] = steps.map((step) => {
        const appointmentCreationDate = parseDate(step.appointmentCreationTimestamp);
        const creationDate = parseDate(getProjectStepCreationTimestamp(step));

        const formattedAppointmentCreationDate = step.appointmentCreationTimestamp
          ? this.showCompactVersion
            ? formatDateAndMonth(appointmentCreationDate)
            : formatFullJsDate(appointmentCreationDate)
          : null;
        const formattedCreationDate = getProjectStepCreationTimestamp(step)
          ? this.showCompactVersion
            ? formatDateAndMonth(creationDate)
            : formatFullJsDate(creationDate)
          : null;

        return joinToString(
          [
            formattedAppointmentCreationDate,
            formattedCreationDate,
          ],
          "\n"
        );
      });

      rowContent["projectStep"] = projectStep;

      if (projectStep.downloadRefExternalPdfReport) {
        rowContent["downloadRefExternalPdfReport"] =
          projectStep.downloadRefExternalPdfReport;
      }

      if (projectStep.downloadRefServicesCsv) {
        rowContent["downloadRefServicesCsv"] =
          projectStep.downloadRefServicesCsv;
      }

      if (projectStep.downloadRefInternalPdfReport) {
        rowContent["downloadRefInternalPdfReport"] =
          projectStep.downloadRefInternalPdfReport;
      }

      if (projectStep.downloadRefHyDryCertificate) {
        rowContent["downloadRefHyDryCertificate"] =
          projectStep.downloadRefHyDryCertificate;
      }

      if (projectStep.downloadAllRefs) {
        rowContent["downloadAllRefs"] =
          projectStep.downloadAllRefs;
      }
    }

    const dependencies = this.stepDependencies.get(stepIndex) ?? [];

    rowContent["stepStatus"] = this.getProjectStepStatus(
      stepIndex,
      projectStep,
      resultMap,
      dependencies,
      projectDetails
    );

    if (this.isAppointmentStepIndex(stepIndex)) {
      rowContent["parameters"] = steps
        .map((step) => {
          return {
            description: this.getAppointmentParameter(stepIndex, step),
            date: step.appointmentDate
              ? parseDate(step.appointmentDate)
              : new Date(),
          };
        })
        //.sort((a1, a2) => a1.date.getTime() - a2.date.getTime())
        .map((a) => a.description);

      rowContent["appointments"] = steps;
    } else {
      rowContent["parameters"] = steps.map((step) =>
        this.getParameters(step, stepIndex)
      );
    }

    if (projectStep && this.isAppointmentStepIndex(stepIndex)) {
      rowContent["stepStatus"] = steps.some((step) => !step.completed)
        ? this.hasStepResponsibleUser(projectStep)
          ? ProjectStepStatus.APPOINTMENT_PENDING
          : ProjectStepStatus.APPOINTMENT_INFO_MISSING
        : ProjectStepStatus.DONE;

      rowContent["appointmentDate"] = projectStep.appointmentDate
        ? parseDate(projectStep.appointmentDate).getTime()
        : Number.MIN_VALUE;
    }

    if (!this.showCompactVersion) {
      if (stepIndex == 12 || stepIndex == 13) {
        const dryingStarted = hasStepStarted(this.projectSteps["11"]);
        const projectFinished = hasStepStarted(this.projectSteps["18"]);
        const dismantlingStarted = hasStepStarted(this.projectSteps["14"]);
        const dryingConcluded = hasStepStarted(this.projectSteps["15"]);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const isDryingSetupReopened = this.projectSteps["11"]?.find((step: any) => step.phaseReOpened === true);

        if (dryingStarted && !projectFinished) {
          const currentStatus = rowContent["stepStatus"];

          if (dismantlingStarted && !dryingConcluded) {
            // While dismantling is actively running (step 14 started, step 15 not done),
            // lock step 12 unless it was already completed (green) or has an appointment (red).
            if (stepIndex === 12
              && currentStatus !== ProjectStepStatus.DONE
              && currentStatus !== ProjectStepStatus.APPOINTMENT_PENDING
              && currentStatus !== ProjectStepStatus.APPOINTMENT_INFO_MISSING
            ) {
              rowContent["stepStatus"] = ProjectStepStatus.LOCKED;
            }
          } else if (dryingConcluded) {
            // After drying is concluded (step 15 done), keep step 12 schedulable
            // for consistency with the project tab.
            if (
              stepIndex === 12 &&
              currentStatus === ProjectStepStatus.LOCKED &&
              !isDryingSetupReopened
            ) {
              rowContent["stepStatus"] = ProjectStepStatus.OPEN;
            }
          } else if (
            stepIndex === 12 &&
            currentStatus === ProjectStepStatus.LOCKED &&
            !isDryingSetupReopened
          ) {
            // No other phase running — unlock step 12
            rowContent["stepStatus"] = ProjectStepStatus.OPEN;
          }
        }
      } else if (this.isIndexOfContactPersonAppointmentStep(stepIndex)) {
        if ( 
          rowContent["stepStatus"] === ProjectStepStatus.DONE &&
          !this.isStepCompletedForAllContactPersons(
            stepIndex,
            true,
            projectDetails
          )
        ) {
          rowContent["stepStatus"] = ProjectStepStatus.OPEN;
        }
      } else if (stepIndex === 18) {
        if (
          rowContent["stepStatus"] === ProjectStepStatus.DONE &&
          !this.isStepCompletedForAllContactPersons(stepIndex, false, projectDetails)
        ) {
          rowContent["stepStatus"] = ProjectStepStatus.OPEN;
        }
      }

    }

    // Step 15 (drying conclusion result): reopen when completed but
    // devices are still assigned (e.g. new device assigned after packup).
    if (stepIndex === 15
      && rowContent["stepStatus"] === ProjectStepStatus.DONE
      && !this.areAllAssignmentsClosed
    ) {
      rowContent["stepStatus"] = ProjectStepStatus.OPEN;
    }

    //Check if damage assessment or leak detection has started and if so set matching appointment buttons to done
    if (stepIndex == 4 || stepIndex == 6) {
      const currentProjectPhase = getCurrentProjectPhase(projectDetails);

      if (
        rowContent["stepStatus"] != ProjectStepStatus.APPOINTMENT_PENDING &&
        ((stepIndex == 4 &&
          currentProjectPhase >= ProjectPhase.LEAK_DETECTION) ||
          (stepIndex == 6 &&
            currentProjectPhase >= ProjectPhase.DAMAGE_ASSESSMENT))
      ) {
        if (this.hasStepDateAndResponsibleUser(projectStep)) {
          rowContent["stepStatus"] = ProjectStepStatus.DONE;
        }
      }
    }

    /* if (!this.stepDependencies.has(stepIndex)) {
      rowContent["stepStatus"] = ProjectStepStatus.LOCKED;
    } */

    let isPhaseReopened = false;

    // Check if a phase is reopened, then mark the finalise step as open
    if (this.shouldCheckForReopenedPhases(stepIndex)) {
      const step = Array.isArray(projectStep) 
        ? projectStep.find((ps) => ps.phaseReOpened === true) 
        : projectStep;

      const stepStatus = rowContent["stepStatus"];
      isPhaseReopened = step?.phaseReOpened === true;
      if (stepStatus === ProjectStepStatus.DONE && isPhaseReopened) {
        rowContent["stepStatus"] = ProjectStepStatus.OPEN;
      }
    }

    // Check if a phase is suspended, then mark the finalise step as red.
    // Each entry of a contact-person step is judged with ITS OWN phaseReOpened flag via
    // getIsStepSuspended, which also compares the suspend/resume timestamps instead of
    // assuming a suspend cleared `resumedBy`.
    if (this.shouldCheckForSuspendedPhases(stepIndex)) {
      const isPhaseSuspended = Array.isArray(projectStep)
        ? projectStep.some((ps) => getIsStepSuspended(ps, ps?.phaseReOpened))
        : getIsStepSuspended(projectStep, isPhaseReopened);

      if (isPhaseSuspended) {
        rowContent["stepStatus"] = ProjectStepStatus.SUSPENDED;
      }
    }

    let row;
    try {
      row = this.sortStepDetailsByTimestamp(rowContent);
    } catch (e) {
      row = rowContent;
    }

    return row;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hasStepDateAndResponsibleUser(appointmentStep: any) {
    if (!appointmentStep) {
      return false;
    }

    return appointmentStep.responsibleUser && appointmentStep.appointmentDate;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hasStepResponsibleUser(appointmentStep: any) {
    if (!appointmentStep) {
      return false;
    }

    return appointmentStep.responsibleUser;
  }

  createStepRow(
    stepIndex: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    projectSteps: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resultMap: Map<number, any>,
    projectDetails: ProjectDetails
  ) {
    const projectStep = this.getProjectStep(projectSteps[stepIndex.toString()], stepIndex);

    return this.createRowContent(stepIndex, projectStep, resultMap, projectDetails);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getParameters(projectStep: any, stepIndex: number) {
    if (!projectStep) {
      return undefined;
    }

    const contactPersonInfo = this.formatContactPersonInfoFromStep(projectStep);
    const isStepResumed = getIsStepResumed(projectStep, projectStep.phaseReOpened);
    const isStepSuspended = getIsStepSuspended(projectStep, projectStep.phaseReOpened);

    const resourceKey = getResourceKey(projectStep);

    if (stepIndex == 1) {
      const formattedProjectType = formatProjectTypeName(this, this.projectTypeId, this.unfilteredProjectTypes);

      return this.$t("selectProjectType.formatProjectType", [formattedProjectType]).toString();
    }

    if (stepIndex == 2) {
      return this.formatOrderApproval(projectStep.orderAccepted);
    }

    if (stepIndex == 3 && projectStep.damageDescription) {
      return this.$t("projectStatus.damageDescription", [
        projectStep.damageDescription,
      ]);
    }

    if (
      stepIndex == 5
    ) {
      if (
        projectStep.projectState &&
        projectStep.projectState !== null
      ) {
        return this.formatProjectState(projectStep.projectState);
      }
      else if (projectStep) {
        return this.$t(`projectStep.leakDetection${resourceKey}`).toString();
      }
    }

    if (stepIndex == 7) {
      if (projectStep.projectState && projectStep.projectState !== null) {
        return this.formatProjectState(projectStep.projectState)
      }

      if (isStepSuspended || isStepResumed) {
        return this.$t(`projectStep.damageAssessment${resourceKey}`).toString(); 
      }

      // The drying line shows on one tile only: here while the project has no
      // insurance step, then on step 9 once that step is recorded. Only for
      // projects with drying, like the row in the step 7 dialog.
      const metricsLine = this.containsDryingInfo && !this.projectSteps["9"]
        ? this.formatDryingMetricsLine(projectStep, undefined)
        : "";

      const formattedApprovalStatus = metricsLine && !this.showCompactVersion
        ? addFullStop(this.formatDryingApproval(projectStep.approvalStatus, this.containsDryingInfo))
        : this.formatDryingApproval(projectStep.approvalStatus, this.containsDryingInfo);

      const approval = this.$t("projectStatus.approvalStatus", [formattedApprovalStatus]).toString();

      return [approval, metricsLine]
        .filter((line) => !!line)
        .join("\n");
    }

    if (stepIndex == 9) {
      // Second line: drying duration and any drying areas.
      const metricsLine = this.formatDryingMetricsLine(projectStep, projectStep);

      const formattedApprovalStatus = metricsLine && !this.showCompactVersion
        ? addFullStop(this.formatInsuranceOfferResult(projectStep.approvalStatus))
        : this.formatInsuranceOfferResult(projectStep.approvalStatus);

      const approval = this.$t("projectStatus.approvalStatus", [formattedApprovalStatus]).toString();

      return [approval, metricsLine]
        .filter((line) => !!line)
        .join("\n");
    }

    if (stepIndex === 11) {
      let note: string | undefined = undefined;

      if (projectStep.note) {
        note = this.$t("projectStatus.note", [projectStep.note]).toString();
      }

      const description = (contactPersonInfo
        ? this.$t(`projectStep.formatDryingSetup${resourceKey}`, [contactPersonInfo])
        : this.$t(`projectStep.dryingSetup${resourceKey}`)
      ).toString();

      return [description, note].filter((line) => !!line).join(", ");
    }

    if (stepIndex === 13 && projectStep) {
      return (contactPersonInfo
        ? this.$t(`projectStep.formatIntermediateMeasurement${resourceKey}`, [contactPersonInfo])
        : this.$t(`projectStep.intermediateMeasurement${resourceKey}`)
      ).toString();
    }

    if (stepIndex == 15) {
      let note: string | undefined = undefined;

      if (projectStep.note) {
        note = this.$t("projectStatus.note", [projectStep.note]).toString();
      }

      const description = (contactPersonInfo
        ? this.$t(`projectStep.formatDrying${resourceKey}`, [contactPersonInfo])
        : this.$t(`projectStep.dryingPackup${resourceKey}`)
      ).toString();

      return [description, note].filter((line) => !!line).join(", ");
    }

    if (stepIndex == 17) {
      let note: string | undefined = undefined;

      if (projectStep.note) {
        note = this.$t("projectStatus.note", [projectStep.note]).toString();
      }

      const description = (contactPersonInfo
        ? this.$t(`projectStep.formatRepair${resourceKey}`, [contactPersonInfo])
        : this.$t(`projectStep.repair${resourceKey}`)
      ).toString();

      return [description, note].filter((line) => !!line).join(", ");
    }

    if (stepIndex == 18) {
      const projectEndDateTimestap = projectStep.projectEndDate;
      let projectEndDesc: string | undefined = undefined;

      if (projectStep.projectState && projectStep.projectState !== null) {
        return this.formatProjectState(projectStep.projectState)
      }

      if (projectEndDateTimestap) {
        projectEndDesc = this.$t("projectStatus.projectEndDesc", [formatDateShort(
          firestore.Timestamp.fromDate(parseDate(projectEndDateTimestap))
        )]).toString();
      }

      return [contactPersonInfo, projectEndDesc]
        .filter((value) => !!value)
        .join(", ");
    }

    const appointmentString = this.getAppointmentParameter(
      stepIndex,
      projectStep
    );

    if (!appointmentString) {
      return undefined;
    }

    return appointmentString;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAppointmentParameter(stepIndex: number, projectStep: any) {
    if (!this.isAppointmentStepIndex(stepIndex)) {
      return undefined;
    }

    const appointmentCreationTimestamp = projectStep?.appointmentCreationTimestamp;
    const appointmentCreatedBy = projectStep?.appointmentCreatedBy;
    const shouldShowAppointmentDetails = 
      (
        appointmentCreationTimestamp || 
        appointmentCreatedBy || 
        appointmentCreationTimestamp === undefined || 
        appointmentCreatedBy === undefined
      )
        ? true
        : false;

    let appointmentDate: undefined | string = "";
    let responsibleUser: undefined | string = "";
    let contactPersonInfo = "";

    if (projectStep.appointmentDate) {
      const defaultTime = "00:00";
      const time = formatJsTime(
        parseDate(projectStep.appointmentDate)
      );

      appointmentDate = formatJsDate(
        parseDate(projectStep.appointmentDate),
        time !== defaultTime 
      );
    }

    if (projectStep.responsibleUser) {
      responsibleUser = findNickname(projectStep.responsibleUser);
    }

    const showContactPersonInfo = stepIndex >= 10;

    if (showContactPersonInfo) {
      contactPersonInfo = this.formatContactPersonInfoFromStep(projectStep) ?? "";
    }

    const appointmentInfo = (!this.isPortalOnlyProject || (appointmentDate || responsibleUser)) && shouldShowAppointmentDetails
      ? [
          appointmentDate
            ? appointmentDate
            : this.$t("projectStep.appointmentNow").toString(),
          responsibleUser
            ? responsibleUser
            : this.$t("projectStep.appointmentOpenResposibleUser").toString(),
        ].filter((it) => !!it)
      : null;

    const shouldShowPhaseInfo =
      !projectStep.__omitPhaseInfo &&
      (projectStep.completed || (projectStep.createdBy && projectStep.creationTimestamp));

    const dryingInfo = shouldShowPhaseInfo
      ? [
          contactPersonInfo,
          this.$t(`projectStatus.phaseInfo${stepIndex}`).toString(),
        ]
          .filter((value) => !!value)
          .join(": ")
      : null;

    const formattedAppointmentInfo = appointmentInfo
      ? [
          this.$t("projectStatus.formatAppointment", [appointmentInfo.join(", ")]).toString(),
          this.showContactPersonChooser
            ? (projectStep.contactPersonNumber
                ? this.$t("projectLogs.projectState.apartmentNumber", [projectStep.contactPersonNumber]).toString()
                : this.$t("projectLogs.projectState.allApartments").toString())
            : null
        ]
          .filter((value) => !!value)
          .join(" ")
      : null;

    const result = joinToString(
      [
        formattedAppointmentInfo,
        dryingInfo,
        projectStep.note ? this.$t("clientData.formatNote", [projectStep.note]).toString() : null
      ],
      "\n"
    );

    return result;
  }

  get hasDrying() {
    return doesProjectTypeIdHaveDrying(this.projectTypeId);
  }

  getProjectStepStatus(
    stepIndex: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    projectStep: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resultMap: Map<number, any>,
    dependencies: number[],
    projectDetails: ProjectDetails
  ) {
    if (stepIndex === 4 || stepIndex === 5) {
      if (
        !this.containsLeakDetectionInfo && 
        !this.projectSteps["4"]
      ) {
        return ProjectStepStatus.LOCKED;
      }
    }

    if (projectStep && projectStep.creationTimestamp) {

      // TODO: Verify if handling of the result steps is correct.
      /* const appoinmentsIndex = stepIndex - 1;

      if (
        !this.showCompactVersion &&
        this.isIndexOfContactPersonAppointmentStep(appoinmentsIndex)
      ) {
        const appointments = this.projectSteps[`${appoinmentsIndex}`];
        
        if (
          (Array.isArray(appointments) &&
            appointments.some(
              (appointment) => 
                !appointment.completed &&
                !(parseDate(projectStep.creationTimestamp).getMilliseconds() < parseDate(appointment.appointmentCreationTimestamp).getMilliseconds())
            ))
        ) {
          return ProjectStepStatus.OPEN;
        }
      } */

      return ProjectStepStatus.DONE;
    }

    if (dependencies.length == 0) {
      return ProjectStepStatus.OPEN;
    }

    let isAnyDependencyFullfield = false;
    let isStepLocked = false;

    if (stepIndex == 3 && this.projectSteps["7"]) {
      isStepLocked = true;
    }

    if (stepIndex == 8) {
      const approvalStep = resultMap.get(7)?.projectStep;

      if (approvalStep && approvalStep.approvalStatus !== 1) {
        isStepLocked = true;
      }

      if (this.projectSteps["9"]) {
        isStepLocked = true;
      }
    }

    /* if (stepIndex === 11) {
      const approvalStep = resultMap.get(7)?.projectStep;

      if (approvalStep && approvalStep.approvalStatus === 2) {
        isAnyDependencyFullfield = true;
      }
    } */

    if (stepIndex >= 10) {
      if (!this.isPortalOnlyProject) {
        const approvalStep = resultMap.get(7)?.projectStep;
        const insuranceResultStep = resultMap.get(9)?.projectStep;

        if (!approvalStep) {
          isStepLocked = true;
        } else if (approvalStep.approvalStatus === 0) {
          isStepLocked = true;
        } else if (approvalStep.approvalStatus === 1) {
          if (!insuranceResultStep || insuranceResultStep.approvalStatus !== 2) {
            isStepLocked = true;
          }
        }
      }
      else {
        const approvalStep = resultMap.get(2)?.projectStep;

        if (!approvalStep || approvalStep.approvalStatus === 0) {
          isStepLocked = true;
        }
      }
    }

    // For portal-only projects: allow concluding without completing the drying
    // workflow when all device assignments are closed and neither the
    // drying setup (step 10) nor drying packup (step 14) has started.
    const dryingSetupStarted = hasStepStarted(this.projectSteps["10"]);
    const dryingPackupStarted = hasStepStarted(this.projectSteps["14"]);
    const canConcludeEarly = this.isPortalOnlyProject
      && this.areAllAssignmentsClosed
      && !dryingSetupStarted
      && !dryingPackupStarted;

    // Once a measurement (step 12) has been started, the measurement result (step 13)
    // must be completed before proceeding with any subsequent drying steps.
    const measurementStarted = hasStepStarted(this.projectSteps["12"]);
    const measurementResultComplete = hasStepStarted(this.projectSteps["13"]);
    if (stepIndex >= 14 && measurementStarted && !measurementResultComplete) {
      isStepLocked = true;
    }

    if (stepIndex == 18 && !canConcludeEarly) {
      for (let i = 10; i < 18; i++) {
        if (i !== 12 && i !== 13 && i !== 16) {
          const stepStatus = resultMap.get(i)?.stepStatus;

          if (
            stepStatus === ProjectStepStatus.OPEN ||
            stepStatus === ProjectStepStatus.APPOINTMENT_PENDING
          ) {
            isStepLocked = true;
            break;
          }
        }
      }
    }

    const isRepairActionNotNeeded = 
      projectDetails.projectConclusionData?.repairAction === RepairAction.NOT_NEEDED;
    const isLastStepLocked =
      (
        (
          projectDetails.projectConclusionData?.repairAction &&
          projectDetails.projectConclusionData.repairAction !== RepairAction.NOT_NEEDED
        ) ||
        this.isRepairOnlyProject
      ) &&
      !this.projectSteps["17"];

    if (stepIndex == 16 && isRepairActionNotNeeded) {
      isStepLocked = true;
    }

    if (
      stepIndex == 18 && 
      isLastStepLocked &&
      !canConcludeEarly
    ) {
      isStepLocked = true;
    }

    dependencies.forEach((dependencyStepIndex) => {
      const previousStep = resultMap.get(dependencyStepIndex);
      
      if (previousStep) {
        if (
          previousStep.step === 2 &&
          previousStep.projectStep?.orderAccepted === 0
        ) {
          isStepLocked = true;
        } else if (previousStep.step === 7) {
          if (previousStep.projectStep?.approvalStatus === 0) {
            isStepLocked = true;
          } else if (
            previousStep.projectStep?.approvalStatus === 2 &&
            stepIndex === 9
          ) {
            isStepLocked = true;
          }
        }

        if (previousStep.dateTime &&
          (
            !this.isAppointmentStepIndex(previousStep.step) ||
            previousStep.projectStep?.completed
          )
        ) {
          isAnyDependencyFullfield = true;
        }
      }
    });

    if ((stepIndex == 16 || stepIndex == 18)) {
      const isDryingSetupFinished = this.isPortalOnlyProject
        ? hasStepStarted(this.projectSteps["11"])
        : projectDetails.setupDryingData?.complete === true;
      const isDryingPackupFinished = this.isPortalOnlyProject
        ? hasStepStarted(this.projectSteps["15"])
        : projectDetails.packupDevicesData?.complete === true;

      const isDryingPhaseFinished =  isDryingSetupFinished && isDryingPackupFinished;

      if (!isDryingPhaseFinished && this.hasDrying && !(stepIndex == 18 && canConcludeEarly)) {
        isStepLocked = true;
      }
    }

    const isAnyPreviousPhaseOpen = Array.from(resultMap.values()).some(value => 
      value.step < stepIndex && (
        value.projectStep?.phaseReOpened === true ||
        getIsStepResumed(value.projectStep, value.projectStep?.phaseReOpened) ||
        getIsStepSuspended(value.projectStep, value.projectStep?.phaseReOpened)
      )
    );

    if (isAnyPreviousPhaseOpen) {
      isStepLocked = true;
    }

    const lastStepIndex = Math.max(...Array.from(this.stepDependencies.keys()));

    if (this.projectSteps[lastStepIndex.toString()]) {
      return ProjectStepStatus.LOCKED;
    }
    if (!this.showCompactVersion && this.isAppointmentStepIndex(stepIndex)) {
      const resultStepIndex = stepIndex == 12 ? 15 : stepIndex + 1;

      const resultStep = this.getProjectStep(this.projectSteps[resultStepIndex.toString()], stepIndex);
      const isProjectFinished = this.projectSteps[lastStepIndex.toString()];

      if (isProjectFinished) {
        isStepLocked = true;
      } else if (resultStep) {
        if (Array.isArray(resultStep)) {
          isStepLocked = this.isStepCompletedForAllContactPersons(
            resultStepIndex,
            false,
            projectDetails
          );
        } else if (resultStep.creationTimestamp) {
          isStepLocked = true;
        }
      }
    }

    // When early conclude is allowed, treat the dependency as fulfilled
    // since step 15 (the formal dependency for step 18) will never be completed.
    if (stepIndex == 18 && canConcludeEarly) {
      isAnyDependencyFullfield = true;
    }

    return isAnyDependencyFullfield && !isStepLocked
      ? ProjectStepStatus.OPEN
      : ProjectStepStatus.LOCKED;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  showCreationDetails(step: any) {
    this.onItemClicked(step);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  showStepDetails(step: any) {
    this.onItemClicked(step);
  }

  async onDownloadReportData(ref?: string | null) {
    if (!ref) {
      return;
    }

    await downloadReportData(ref);
  }

  beforeDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription();
    });
  }

  async markAllContactPersonAppointmentsAsCompleted() {
    const projectSteps = this.projectSteps;

    const appointmentsToMarkAsCompleted = this.cotactPersonAppointmentIndices
      .map((index) => {
        return {
          index: index,
          projectStep: projectSteps[index.toString()],
        };
      })
      .filter((stepData) => !!stepData.projectStep)
      .map((stepData) => {
        let projecStep = stepData.projectStep;

        if (!Array.isArray(projecStep)) {
          projecStep = [projecStep];
        }

        return (
          projecStep
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .filter((appointment: any) => !appointment.completed)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((appointment: any) => {
              appointment.completed = true;

              return {
                appointment: appointment,
                index: stepData.index,
              };
            })
        );
      })
      .flat();

    return Promise.all(
      appointmentsToMarkAsCompleted.map((appointmentData) =>
        this.saveContactPersonAppointment(
          appointmentData.index,
          appointmentData.appointment,
          true
        )
      )
    );
  }

  /**
   * Splits entries that have both appointment and creation data into two separate objects.
   * @param {Array<Object>} steps
   * @returns {Array<Object>}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  splitAppointmentAndCreationData(step: any[]) {
    const appointmentFields = [
      "appointmentCreationTimestamp",
      "appointmentCreatedBy",
      "appointmentDate",
      "responsibleUser",
    ];
    const creationFields = ["creationTimestamp", "createdBy"];

    return step.flatMap(item => {
      const hasAppointmentData = appointmentFields.some(f => !!item[f]);
      const hasCreationData    = creationFields.some(f => !!item[f]);

      // If it has both kinds of data, emit two objects:
      if (hasAppointmentData && hasCreationData) {
        // 1) keep only appointment fields
        // Mark appointment-only rows to avoid showing phase text twice.
        const onlyAppt = { ...item, __omitPhaseInfo: true };
        creationFields.forEach(f => { onlyAppt[f] = ""; });

        // 2) keep only creation fields
        const onlyCreate = { ...item };
        appointmentFields.forEach(f => { onlyCreate[f] = ""; });
        // additionalUsers is appointment (Termin) metadata like responsibleUser — keep it
        // on the appointment copy only; clear to [] (not "") on the creation copy.
        onlyCreate.additionalUsers = [];

        return [onlyAppt, onlyCreate];
      }

      // Otherwise, leave the object as-is
      return [item];
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expandAppointmentStep(step: any) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resultMap = new Map<number, any>();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let appointmentRows: any[] = [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let stepResultRows: any[] = [];

    const stepIndex = step.step;

    const steps = this.projectStatus?.projectSteps ?? {};
    const projectDetails = this.projectDetails;

    const appointmentResultIndex = stepIndex + 1;
    const appointmentResult = steps[appointmentResultIndex.toString()];

    let latestAppointmentResultTimestamp = 0;

    const completedSteps: number[] = [];
    const contactPersonNumbersOfCompletedOrPendingAppointments = new Set<
      number
    >();
    const isInterediateMeasurmentsAppointmentStep = stepIndex === 12;

    if (appointmentResult) {
      const extendedAppointmentResult = appointmentResultIndex === 17 && appointmentResult.extendedRepairResult
        ? appointmentResult.extendedRepairResult
        : appointmentResult;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resultArray: any[] = (Array.isArray(extendedAppointmentResult)
          ? extendedAppointmentResult
          : [extendedAppointmentResult]
        )
        .slice()
        .sort((r1, r2) => {
          return parseDate(r1.creationTimestamp).getTime() - parseDate(r2.creationTimestamp).getTime();
        });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      resultArray.forEach((value: any) => {
        const row = this.createRowContent(
          appointmentResultIndex,
          value,
          resultMap,
          projectDetails
        );

        completedSteps.push(value.contactPersonNumber ?? 0);

        const resultTimestamp = parseDate(value.creationTimestamp).getTime();
        const contactPersonNumber = value.contactPersonNumber ?? 0;

        row["appointmentDate"] = resultTimestamp;

        /* if (this.showContactPersonChooser) {
          row["parameters"] = this.formatContactPerson(contactPersonNumber);
        } */

        latestAppointmentResultTimestamp = Math.max(
          latestAppointmentResultTimestamp,
          resultTimestamp
        );

        if (stepResultRows.find((step) => step.step === row.step)) {
          row["showButton"] = false;
          stepResultRows[stepResultRows.length - 1]["addMarginAfter"] = false;
        }

        stepResultRows.push(row);

        if (!isInterediateMeasurmentsAppointmentStep) {
          contactPersonNumbersOfCompletedOrPendingAppointments.add(
            contactPersonNumber
          );
        }
      });
    }

    stepResultRows = stepResultRows.sort((r1, r2) => {
      return (r1.appointmentDate ?? 0) - (r2.appointmentDate ?? 0);
    });

    const isDryingComplete =
      projectDetails.projectConclusionData?.dryingSuccessful !== null;

    const isProjectComplete = hasStepStarted(this.projectSteps["18"]);

    if (isInterediateMeasurmentsAppointmentStep && isProjectComplete) {
      return stepResultRows;
    }

    let isAppointmentStepComplete = false;

    if (isProjectComplete) {
      isAppointmentStepComplete = true;
    } else if (stepIndex == 10) {
      isAppointmentStepComplete = this.isPortalOnlyProject
        ? !!steps["11"]
        : projectDetails.setupDryingData?.complete === true;
    } /* else if (stepIndex == 12) {  // We always allow scheduling measurement 
      isAppointmentStepComplete =
        projectDetails.projectConclusionData?.dryingSuccessful != null;
    } */ else if (stepIndex == 14) {
      isAppointmentStepComplete = isDryingComplete || steps["15"];
    } else if (stepIndex == 16) {
      isAppointmentStepComplete = steps["17"];
    }

    if (step.appointments) {
      const appointmentsAfterSplit = this.splitAppointmentAndCreationData(step.appointments);

      for (const appointment of appointmentsAfterSplit) {
        const rowContent = this.createRowContent(
          step.step,
          appointment,
          resultMap,
          projectDetails
        );

        if (
          rowContent.stepStatus !== ProjectStepStatus.DONE ||
          completedSteps.indexOf(appointment.contactPersonNumber) < 0 ||
          stepIndex == 12
        ) {
          rowContent["showButton"] = false;
          rowContent["addMarginAfter"] = false;

          if (stepIndex == 12) {
            // Show red if appointment exists, otherwise keep yellow (OPEN)
            if (!appointment.completed && appointment.appointmentDate) {
              rowContent["stepStatus"] = this.hasStepResponsibleUser(appointment)
                ? ProjectStepStatus.APPOINTMENT_PENDING
                : ProjectStepStatus.APPOINTMENT_INFO_MISSING;
            } else {
              rowContent["stepStatus"] = ProjectStepStatus.OPEN;
            }
          }

          appointmentRows.push(rowContent);
          contactPersonNumbersOfCompletedOrPendingAppointments.add(
            appointment.contactPersonNumber ?? 0
          );
        }
      }

      // For GZ projects: when an appointment is completed but no result step exists for that
      // contact person, show an OPEN result row (FERTIG button) so the user can complete the phase.
      if (this.isPortalOnlyProject && !isInterediateMeasurmentsAppointmentStep) {
        const completedCPsWithoutResult = appointmentsAfterSplit
          .filter((a: { completed: boolean; contactPersonNumber: number }) =>
            a.completed === true &&
            completedSteps.indexOf(a.contactPersonNumber) < 0
          )
          .map((a: { contactPersonNumber: number }) => a.contactPersonNumber);

        const addedResultCPs = new Set<number>();
        for (const cp of completedCPsWithoutResult) {
          if (addedResultCPs.has(cp)) continue;
          addedResultCPs.add(cp);

          const resultRow = this.createRowContent(
            appointmentResultIndex,
            { contactPersonNumber: cp },
            resultMap,
            projectDetails
          );
          resultRow["stepStatus"] = ProjectStepStatus.OPEN;
          stepResultRows.push(resultRow);
        }
      }
    }

    let hasAppointmentForAllContactPersons = contactPersonNumbersOfCompletedOrPendingAppointments.has(
      0
    );

    if (!hasAppointmentForAllContactPersons) {
      hasAppointmentForAllContactPersons = true;

      projectDetails.clientData.contactPersons.forEach(
        (contactPersonInfo) => {
          if (
            !contactPersonNumbersOfCompletedOrPendingAppointments.has(
              contactPersonInfo.contactPersonNumber
            )
          ) {
            hasAppointmentForAllContactPersons = false;
          }
        }
      );
    }

    // Sort such that actual appointment entries (with a valid appointmentDate) come first,
    // ordered by appointmentDate, then creation-phase entries ordered by creationTimestamp.
    appointmentRows = appointmentRows.sort((r1, r2) => {
      const apptDate1 = r1.appointmentDate ?? Number.MIN_VALUE;
      const apptDate2 = r2.appointmentDate ?? Number.MIN_VALUE;
      const hasAppt1 = apptDate1 > Number.MIN_VALUE;
      const hasAppt2 = apptDate2 > Number.MIN_VALUE;

      // Appointment entries first
      if (hasAppt1 && !hasAppt2) return -1;
      if (!hasAppt1 && hasAppt2) return 1;

      // Both are appointments: sort by appointmentDate ascending
      if (hasAppt1 && hasAppt2) {
        return apptDate1 - apptDate2;
      }

      // Neither are appointments: sort by creationTimestamp ascending
      const created1 = r1.projectStep?.creationTimestamp
        ? parseDate(r1.projectStep.creationTimestamp).getTime()
        : 0;
      const created2 = r2.projectStep?.creationTimestamp
        ? parseDate(r2.projectStep.creationTimestamp).getTime()
        : 0;
      return created1 - created2;
    });

    if (appointmentRows && appointmentRows.length > 0) {
      appointmentRows[0]["showButton"] = true;
      appointmentRows[appointmentRows.length - 1]["addMarginAfter"] = true;
    }

    if (!isAppointmentStepComplete && !hasAppointmentForAllContactPersons) {
      const rowContent = this.createRowContent(stepIndex, {}, resultMap, projectDetails);

      rowContent["stepStatus"] = ProjectStepStatus.OPEN;

      if (appointmentRows.find((step) => step.step === rowContent.step)) {
        rowContent["showButton"] = false;
        appointmentRows[appointmentRows.length - 1]["addMarginAfter"] = false;
      }
      appointmentRows.push(rowContent);
    }

    return [...stepResultRows, ...appointmentRows];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getContactPersonsNumberCompleted(step: any) {
    const contactPersons = new Set<number>();

    if (!step) {
      return contactPersons;
    }

    const stepArray = Array.isArray(step) ? step : [step];

    stepArray.forEach((step) => {
      contactPersons.add(step.contactPersonNumber ?? 0);
    });

    return contactPersons;
  }

  areAllAppointmentsCompleted(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    step: any,
  ) {
    let isCompleted = true;

    if (!step) {
      return false;
    }

    const stepArray = Array.isArray(step) ? step : [step];

    for (const projectStep of stepArray) {
      if (!projectStep.completed) {
        isCompleted = false;
        
        break;
      }
    }

    return isCompleted;
  }

  hasAppointmentStartedForAllContactPersons(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    step: any,
    noOfContactPersons = 1,
  ) {
    let isCompleted = true;
    let includesDefaultContactPerson = false;

    if (!step) {
      return false;
    }

    const stepArray = Array.isArray(step) ? step : [step];

    for (const projectStep of stepArray) {
      if (projectStep.contactPersonNumber === 0) {
        includesDefaultContactPerson = true;
      }

      if (!projectStep.completed) {
        isCompleted = false;
        
        break;
      }
    }

    if (!includesDefaultContactPerson && stepArray.length < noOfContactPersons) {
      isCompleted = false;
    }

    return isCompleted;
  }

  getDeviceProjectAssignmentsOfContactPerson(contactPerson: ContactPersonInfo) {
    // Both sides are stripped: assignments written by the project app carry the
    // ELECTRICITY_BILLING_ prefix, the real contact persons never do.
    const contactPersonId = stripElectricityBillingPrefix(contactPerson.id);

    // Filters client panel assignments for a given contact person.
    const clientPanelAssignments = this.clientPanelAssignments
      .filter(
        (clientPanelAssignment) =>
          stripElectricityBillingPrefix(clientPanelAssignment.contactPersonId) === contactPersonId
      );

    // Filters generic device assignments for a given contact person.
    const genericDeviceAssignments = this.genericDeviceAssignments
      .filter(
        (genericDeviceAssignment) =>
          stripElectricityBillingPrefix(genericDeviceAssignment.contactPersonId) === contactPersonId
      );

    // Combines client panel assignments and generic device assignments.
    const deviceProjectAssignments = [
      ...clientPanelAssignments,
      ...genericDeviceAssignments
    ];
    
    return deviceProjectAssignments;
  }

  isStepCompletedForAllContactPersons(
    stepIndex: number, 
    isAppointmentStep = false,
    projectDetails: ProjectDetails
  ) {
    if (this.isPortalOnlyProject) {
      const hasAssignmentsForAllContactPersons = !projectDetails.clientData.contactPersons.some(
        (contactPersonInfo) =>
          this.getDeviceProjectAssignmentsOfContactPerson(contactPersonInfo).length === 0
      );

      if (stepIndex === 11) {
        return hasAssignmentsForAllContactPersons;
      }
      // TODO: Remove after changes have been tested.
      /* else if (stepIndex === 14) {
        return projectDetails.clientData.contactPersons.every(
          (contactPersonInfo) => 
            this.getDeviceProjectAssignmentsOfContactPerson(contactPersonInfo)
              .some((assignment) => assignment.assignmentEndDate)
        );
      } */
      else if (stepIndex === 15) {
        return hasAssignmentsForAllContactPersons && this.areAllAssignmentsClosed;
      }
    }

    const doMultipleContactPersonsExist = this.showContactPersonChooser;
    const step = this.projectSteps[stepIndex.toString()];

    if (!step) {
      return false;
    } else if (!doMultipleContactPersonsExist) {
      return true;
    }

    if (!this.isPortalOnlyProject && isAppointmentStep) {
      return this.areAllAppointmentsCompleted(
        step
      );
    }

    const contactPersons = this.getContactPersonsNumberCompleted(step);

    if (contactPersons.has(0)) {
      return true;
    }

    return (
      projectDetails.clientData.contactPersons.length ===
      contactPersons.size
    );
  }
}
